import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef
} from 'react';
import { Menu, Item, Text, View, Divider } from '@adobe/react-spectrum';
import { Selection } from '@react-types/shared';
import styles from './FilterSection.module.scss';
import MenuCheckbox from './MenuCheckbox';

interface CategoryProductFilterProps {
  categoryToProductMap: Record<string, string[]>;
  selectedCategories: Set<string>;
  selectedProducts: Set<string>;
  onSelectionChange: (categories: string[], products: string[]) => void;
}

// Search icon SVG component
const SearchIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18.5303 17.4697L13.447 12.3865C14.4164 11.1851 15 9.66003 15 8C15 4.14062 11.8594 1 8 1C4.14062 1 1 4.14061 1 7.99999C1 11.8594 4.14062 15 8 15C9.66003 15 11.1851 14.4164 12.3865 13.447L17.4697 18.5303C17.6162 18.6767 17.8076 18.75 18 18.75C18.1924 18.75 18.3838 18.6768 18.5303 18.5303C18.8232 18.2373 18.8232 17.7627 18.5303 17.4697ZM8 13.5C4.96777 13.5 2.5 11.0322 2.5 7.99999C2.5 4.96776 4.96777 2.49999 8 2.49999C11.0322 2.49999 13.5 4.96776 13.5 7.99999C13.5 11.0322 11.0322 13.5 8 13.5Z"
      fill="#717171"
    />
  </svg>
);

// Clear icon SVG component
const ClearIcon = () => (
  <svg
    width="8"
    height="8"
    viewBox="0 0 8 8"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.95 1.05L5 4L7.95 6.95C8.05 7.05 8.05 7.25 7.95 7.35C7.85 7.45 7.65 7.45 7.55 7.35L4.6 4.4L1.65 7.35C1.55 7.45 1.35 7.45 1.25 7.35C1.15 7.25 1.15 7.05 1.25 6.95L4.2 4L1.25 1.05C1.15 0.95 1.15 0.75 1.25 0.65C1.35 0.55 1.55 0.55 1.65 0.65L4.6 3.6L7.55 0.65C7.65 0.55 7.85 0.55 7.95 0.65C8.05 0.75 8.05 0.95 7.95 1.05Z"
      fill="#292929"
    />
  </svg>
);

// Generate unique keys for all items - moved outside component to prevent recreation
const generateKey = (type: 'category' | 'product', value: string) => {
  return `${type}:${value}`;
};

// Parse key to get type and value - moved outside component
const parseKey = (key: string): { type: string; value: string } => {
  const [type, ...valueParts] = key.split(':');
  return { type, value: valueParts.join(':') };
};

export function CategoryProductFilter({
  categoryToProductMap,
  selectedCategories,
  selectedProducts,
  onSelectionChange
}: CategoryProductFilterProps) {
  // Local state for managing selections
  const [localSelectedKeys, setLocalSelectedKeys] = useState<Set<string>>(
    new Set()
  );

  // Search state
  const [searchText, setSearchText] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Memoize categories and products arrays to avoid recalculating
  const { allCategories, allProducts } = useMemo(() => {
    const categories = Object.keys(categoryToProductMap);
    const products = Object.values(categoryToProductMap).flat();
    return { allCategories: categories, allProducts: products };
  }, [categoryToProductMap]);

  // Filter categories and products based on search text
  const filteredData = useMemo(() => {
    if (!searchText.trim()) {
      return { categories: allCategories, productMap: categoryToProductMap };
    }

    const searchLower = searchText.toLowerCase();
    const filteredCategories: string[] = [];
    const filteredProductMap: Record<string, string[]> = {};

    allCategories.forEach(category => {
      const categoryMatches = category.toLowerCase().includes(searchLower);
      const products = categoryToProductMap[category] || [];
      const matchingProducts = products.filter(product =>
        product.toLowerCase().includes(searchLower)
      );

      // Include category if it matches OR if any of its products match
      if (categoryMatches || matchingProducts.length > 0) {
        filteredCategories.push(category);
        // If category matches, show all products; otherwise show only matching products
        filteredProductMap[category] = categoryMatches
          ? products
          : matchingProducts;
      }
    });

    return { categories: filteredCategories, productMap: filteredProductMap };
  }, [searchText, allCategories, categoryToProductMap]);

  // Sync local state with props
  useEffect(() => {
    const keys = new Set<string>();

    // Add "all" if all items are selected
    const allCategoriesSelected =
      allCategories.length > 0 &&
      allCategories.every(cat => selectedCategories.has(cat));
    const allProductsSelected =
      allProducts.length > 0 &&
      allProducts.every(prod => selectedProducts.has(prod));

    if (allCategoriesSelected && allProductsSelected) {
      keys.add('all');
    }

    // Add selected categories
    selectedCategories.forEach(cat => {
      keys.add(generateKey('category', cat));
    });

    // Add selected products
    selectedProducts.forEach(prod => {
      keys.add(generateKey('product', prod));
    });

    setLocalSelectedKeys(keys);
  }, [selectedCategories, selectedProducts, allCategories, allProducts]);

  // Handle selection changes - memoized with useCallback
  const handleSelectionChange = useCallback(
    (selection: Selection) => {
      if (!(selection instanceof Set)) {
        onSelectionChange([], []);
        return;
      }

      const currentSelection = new Set(
        Array.from(selection).map(s => String(s))
      );

      // Check if "All" was just selected or deselected
      const allJustSelected =
        currentSelection.has('all') && !localSelectedKeys.has('all');
      const allJustDeselected =
        !currentSelection.has('all') && localSelectedKeys.has('all');

      let finalCategories: string[] = [];
      let finalProducts: string[] = [];

      if (allJustSelected) {
        // Select all categories and products - use memoized arrays
        finalCategories = allCategories;
        finalProducts = allProducts;
      } else if (allJustDeselected) {
        // Deselect everything
        finalCategories = [];
        finalProducts = [];
      } else {
        // Process individual selections
        const newCategories = new Set<string>();
        const newProducts = new Set<string>();

        currentSelection.forEach(key => {
          if (key === 'all') return;

          const { type, value } = parseKey(key);
          if (type === 'category') {
            newCategories.add(value);
          } else if (type === 'product') {
            newProducts.add(value);
          }
        });

        // Check if a category was just selected
        allCategories.forEach(category => {
          const categoryKey = generateKey('category', category);
          const wasSelected = localSelectedKeys.has(categoryKey);
          const isSelected = currentSelection.has(categoryKey);

          if (!wasSelected && isSelected) {
            // Category was just selected - select all its products
            const products = categoryToProductMap[category] || [];
            products.forEach(prod => newProducts.add(prod));
          } else if (wasSelected && !isSelected) {
            // Category was just deselected - deselect all its products
            const products = categoryToProductMap[category] || [];
            products.forEach(prod => newProducts.delete(prod));
          }
        });

        // Auto-select categories if all their products are selected
        Object.entries(categoryToProductMap).forEach(([category, products]) => {
          if (products.length > 0) {
            const allProductsSelected = products.every(prod =>
              newProducts.has(prod)
            );
            if (allProductsSelected) {
              newCategories.add(category);
            } else {
              newCategories.delete(category);
            }
          } else {
            // Empty category - keep its selection state
            if (currentSelection.has(generateKey('category', category))) {
              newCategories.add(category);
            }
          }
        });

        finalCategories = Array.from(newCategories);
        finalProducts = Array.from(newProducts);
      }

      onSelectionChange(finalCategories, finalProducts);
    },
    [
      localSelectedKeys,
      allCategories,
      allProducts,
      categoryToProductMap,
      onSelectionChange
    ]
  );

  // Handle specific "All" click - memoized with useCallback
  const handleAllClick = useCallback(() => {
    const allSelected =
      allCategories.length > 0 &&
      allProducts.length > 0 &&
      allCategories.every(cat => selectedCategories.has(cat)) &&
      allProducts.every(prod => selectedProducts.has(prod));

    if (allSelected) {
      // Deselect all
      onSelectionChange([], []);
    } else {
      // Select all - use memoized arrays
      onSelectionChange(allCategories, allProducts);
    }
  }, [
    allCategories,
    allProducts,
    selectedCategories,
    selectedProducts,
    onSelectionChange
  ]);

  // Memoize disabled keys array (only dividers should be disabled, not search field)
  const disabledKeys = useMemo(
    () => [...allCategories.map(cat => `divider-${cat}`)],
    [allCategories]
  );

  // Build the menu items
  const menuItems = useMemo(() => {
    const items: JSX.Element[] = [];

    // Add search field as first item
    items.push(
      <Item key="search-field" data-testid="category-search-field-item">
        <Text UNSAFE_className={styles.searchFieldMenuItem}>
          <div className={styles.categorySearchField}>
            <div className={styles.categorySearchIconContainer}>
              <SearchIcon />
            </div>
            <input
              ref={searchInputRef}
              type="text"
              className={styles.categorySearchInput}
              placeholder="Search product"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              onKeyDown={e => e.stopPropagation()}
              data-testid="category-search-input"
            />
            <button
              className={styles.categoryClearButton}
              onClick={() => {
                setSearchText('');
                searchInputRef.current?.focus();
              }}
              aria-label="Clear search"
              data-testid="category-search-clear"
              data-hidden={!searchText}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="8"
                height="8"
                viewBox="0 0 8 8"
                fill="none"
              >
                <path
                  d="M5.23779 4L7.69384 1.54395C8.03565 1.20215 8.03565 0.647469 7.69384 0.305669C7.35204 -0.0361311 6.79834 -0.0361311 6.45654 0.305669L4 2.76221L1.54346 0.305659C1.20166 -0.0361311 0.647955 -0.0361311 0.306155 0.305659C-0.0356453 0.647449 -0.0356353 1.20215 0.306155 1.54395L2.7622 4L0.306155 6.45605C-0.0356453 6.79785 -0.0356453 7.35253 0.306155 7.69433C0.477055 7.86523 0.700685 7.95019 0.924805 7.95019C1.14892 7.95019 1.37255 7.86523 1.54345 7.69433L3.99999 5.23779L6.45653 7.69433C6.62743 7.86523 6.85106 7.95019 7.07518 7.95019C7.2993 7.95019 7.52293 7.86523 7.69383 7.69433C8.03563 7.35253 8.03563 6.79785 7.69383 6.45605L5.23779 4Z"
                  fill="#292929"
                />
              </svg>
            </button>
          </div>
        </Text>
      </Item>
    );

    // Add categories and their products - use filtered data
    filteredData.categories.forEach((category, index) => {
      const categoryKey = generateKey('category', category);
      const products = filteredData.productMap[category] || [];

      // Add category item
      items.push(
        <Item
          key={categoryKey}
          data-testid={`category-${category
            .replace(/[^a-zA-Z0-9]/g, '-')
            .toLowerCase()}`}
        >
          <Text
            UNSAFE_className={`${styles.menuItemText} ${styles.categoryItem}`}
          >
            <View>
              <MenuCheckbox
                isSelected={localSelectedKeys.has(categoryKey)}
                aria-label={`Select ${category} category`}
              />
            </View>
            <strong className={styles.menuItemLabel} title={category}>
              {category}
            </strong>
          </Text>
        </Item>
      );

      // Add products under this category
      products.forEach(product => {
        const productKey = generateKey('product', product);
        items.push(
          <Item
            key={productKey}
            data-testid={`product-${product
              .replace(/[^a-zA-Z0-9]/g, '-')
              .toLowerCase()}`}
          >
            <Text
              UNSAFE_className={`${styles.menuItemText} ${styles.productItem}`}
            >
              <View>
                <MenuCheckbox
                  isSelected={localSelectedKeys.has(productKey)}
                  aria-label={`Select ${product} product`}
                />
              </View>
              <span className={styles.menuItemLabel} title={product}>
                {product}
              </span>
            </Text>
          </Item>
        );
      });

      // Add divider between categories (but not after the last one)
      if (index < filteredData.categories.length - 1) {
        items.push(
          <Item key={`divider-${category}`}>
            <Text>
              <Divider
                orientation="horizontal"
                UNSAFE_className={styles.subMenuDivider}
              />
            </Text>
          </Item>
        );
      }
    });

    return items;
  }, [filteredData, localSelectedKeys]);

  // Memoize onAction handler
  const handleAction = useCallback(
    (key: React.Key) => {
      if (key === 'all') {
        handleAllClick();
      }
    },
    [handleAllClick]
  );

  return (
    <Menu
      data-testid="category-product-filter-menu"
      selectedKeys={localSelectedKeys}
      onSelectionChange={handleSelectionChange}
      onAction={handleAction}
      selectionMode="multiple"
      disabledKeys={disabledKeys}
      UNSAFE_className={styles['subMenu']}
    >
      {menuItems}
    </Menu>
  );
}

CategoryProductFilter.displayName = 'CategoryProductFilter';
